import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { Pencil, Plus, Save, Trash2 } from 'lucide-react'

import { useClient } from '@/entities/client/model/useClient'
import { generatePetId } from '@/shared/lib/pets/generate-pet-id'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { FormControl, FormField, FormItem, FormLabel } from '@/shared/ui/form'
import { Input } from '@/shared/ui/input'
import { Loader } from '@/shared/ui/loader'
import { PetObservationsField } from '@/shared/ui/pet-observations-field'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { type Pet } from '@monorepo/functions/src/types/reservations'

interface ClientPetsProps {
    clientId: string
}

type PetFormData = {
    pets: Pet[]
}

export function ClientPets({ clientId }: ClientPetsProps) {
    const { client, setClientSync: setClient } = useClient(clientId)
    const [isEditing, setIsEditing] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const methods = useForm<PetFormData>({
        defaultValues: {
            pets: client?.pets || [],
        },
    })

    if (!client) return <Loader />

    const handleEditToggle = () => {
        if (!isEditing) {
            methods.reset({ pets: client.pets })
            setIsEditing(true)
        }
    }

    const handleAddPet = () => {
        const currentPets = methods.getValues('pets') || []
        methods.setValue('pets', [
            ...currentPets,
            {
                id: generatePetId(),
                name: '',
                breed: '',
                age: '',
                size: 'pequeño',
                weight: 0,
                sex: 'M',
                isNeutered: false,
                personality: '',
                observations: '',
            },
        ])
    }

    const handleDeletePet = (petIndex: number) => {
        const currentPets = methods.getValues('pets') || []
        methods.setValue(
            'pets',
            currentPets.filter((_, index) => index !== petIndex),
        )
    }

    const onSubmit = async (data: PetFormData) => {
        try {
            setIsSaving(true)
            await setClient({ ...client, ...data })
            setIsEditing(false)
        } finally {
            setIsSaving(false)
        }
    }

    const petsToRender = isEditing ? methods.watch('pets') : client.pets

    return (
        <Card>
            <CardHeader className='flex flex-row items-center justify-between'>
                <CardTitle>Mascotas Asociadas</CardTitle>
                {!isEditing && (
                    <Button variant='outline' onClick={handleEditToggle} className='!mt-0'>
                        <Pencil className='mr-2 h-4 w-4' />
                        Editar Información
                    </Button>
                )}
            </CardHeader>
            <CardContent>
                <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(onSubmit)}>
                        <div className='grid gap-6'>
                            {isEditing && (
                                <div className='flex justify-end'>
                                    <Button type='button' variant='outline' onClick={handleAddPet}>
                                        <Plus className='mr-2 h-4 w-4' />
                                        Añadir Mascota
                                    </Button>
                                </div>
                            )}
                            {petsToRender.map((pet, index) => (
                                <div key={pet.id} className='overflow-hidden rounded-lg border bg-card'>
                                    <div className='flex items-center justify-between border-b bg-muted/50 px-4 py-3'>
                                        <h3 className='text-lg font-semibold'>
                                            {pet.name?.trim() ? pet.name : 'Sin nombre'}
                                        </h3>
                                        {isEditing && (
                                            <Button
                                                type='button'
                                                variant='destructive'
                                                size='sm'
                                                onClick={() => handleDeletePet(index)}
                                            >
                                                <Trash2 className='mr-2 h-4 w-4' />
                                                Eliminar
                                            </Button>
                                        )}
                                    </div>
                                    <div className='bg-background p-4'>
                                        <div className='grid grid-cols-2 gap-x-8 gap-y-4'>
                                            <FormField
                                                control={methods.control}
                                                name={`pets.${index}.name`}
                                                render={({ field }) => (
                                                    <FormItem className='col-span-2'>
                                                        <FormLabel>Nombre</FormLabel>
                                                        <FormControl>
                                                            {isEditing ? (
                                                                <Input {...field} placeholder='Nombre de la mascota' />
                                                            ) : (
                                                                <p>{pet.name?.trim() ? pet.name : 'Sin nombre'}</p>
                                                            )}
                                                        </FormControl>
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={methods.control}
                                                name={`pets.${index}.breed`}
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Raza</FormLabel>
                                                        <FormControl>
                                                            {isEditing ? <Input {...field} /> : <p>{pet.breed}</p>}
                                                        </FormControl>
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={methods.control}
                                                name={`pets.${index}.age`}
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Edad</FormLabel>
                                                        <FormControl>
                                                            {isEditing ? (
                                                                <Input
                                                                    type='text'
                                                                    {...field}
                                                                    onChange={e => field.onChange(e.target.value)}
                                                                    placeholder='Ej: 2 años, 6 meses, 18 meses'
                                                                />
                                                            ) : (
                                                                <p>{pet.age}</p>
                                                            )}
                                                        </FormControl>
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={methods.control}
                                                name={`pets.${index}.size`}
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Tamaño</FormLabel>
                                                        <FormControl>
                                                            {isEditing ? <Input {...field} /> : <p>{pet.size}</p>}
                                                        </FormControl>
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={methods.control}
                                                name={`pets.${index}.sex`}
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Sexo</FormLabel>
                                                        <FormControl>
                                                            {isEditing ? (
                                                                <Select
                                                                    value={field.value}
                                                                    onValueChange={field.onChange}
                                                                >
                                                                    <SelectTrigger>
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value='M'>Macho</SelectItem>
                                                                        <SelectItem value='F'>Hembra</SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                            ) : (
                                                                <p>{pet.sex === 'M' ? 'Macho' : 'Hembra'}</p>
                                                            )}
                                                        </FormControl>
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={methods.control}
                                                name={`pets.${index}.personality`}
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Personalidad</FormLabel>
                                                        <FormControl>
                                                            {isEditing ? (
                                                                <Input
                                                                    {...field}
                                                                    placeholder='Sin personalidad definida'
                                                                />
                                                            ) : (
                                                                <p>{pet.personality || 'Sin personalidad definida'}</p>
                                                            )}
                                                        </FormControl>
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={methods.control}
                                                name={`pets.${index}.weight`}
                                                render={({ field }) => (
                                                    <FormItem className='col-span-2'>
                                                        <FormLabel>Peso (kg)</FormLabel>
                                                        <FormControl>
                                                            {isEditing ? (
                                                                <Input
                                                                    type='number'
                                                                    {...field}
                                                                    onChange={e =>
                                                                        field.onChange(parseFloat(e.target.value))
                                                                    }
                                                                    placeholder='Peso en kg'
                                                                />
                                                            ) : (
                                                                <p>{pet.weight} kg</p>
                                                            )}
                                                        </FormControl>
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={methods.control}
                                                name={`pets.${index}.isNeutered`}
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Estado</FormLabel>
                                                        <FormControl>
                                                            {isEditing ? (
                                                                <Select
                                                                    value={field.value ? 'yes' : 'no'}
                                                                    onValueChange={value =>
                                                                        field.onChange(value === 'yes')
                                                                    }
                                                                >
                                                                    <SelectTrigger>
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value='yes'>Castrado/a</SelectItem>
                                                                        <SelectItem value='no'>
                                                                            No castrado/a
                                                                        </SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                            ) : (
                                                                <p>{pet.isNeutered ? 'Castrado/a' : 'No castrado/a'}</p>
                                                            )}
                                                        </FormControl>
                                                    </FormItem>
                                                )}
                                            />
                                            <PetObservationsField
                                                form={methods}
                                                petIndex={index}
                                                isEditing={isEditing}
                                                currentValue={pet.observations}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {petsToRender.length === 0 && (
                                <p className='text-sm text-muted-foreground'>
                                    No hay mascotas asociadas. Añade una para empezar.
                                </p>
                            )}
                        </div>
                        {isEditing && (
                            <div className='mt-4 flex justify-end gap-2'>
                                <Button variant='outline' type='button' onClick={() => setIsEditing(false)}>
                                    Cancelar
                                </Button>
                                <Button type='submit' disabled={isSaving}>
                                    {isSaving ? (
                                        <Loader />
                                    ) : (
                                        <>
                                            <Save className='mr-2 h-4 w-4' />
                                            Guardar Cambios
                                        </>
                                    )}
                                </Button>
                            </div>
                        )}
                    </form>
                </FormProvider>
            </CardContent>
        </Card>
    )
}
